/**
 * The ornament kit — Indian pattern vocabulary in hairline strokes:
 * the lotus, the block-print border band (diamond · bud · diamond,
 * the bel of fabric borders), and the rangoli chain. Always the same
 * terracotta, always line-drawn — carved, never printed-on.
 */
export function Lotus({
  size = 28,
  className = "",
}: {
  size?: number;
  className?: string;
}) {
  return (
    <svg
      width={size}
      height={size * 0.58}
      viewBox="0 0 48 28"
      fill="none"
      className={className}
      aria-hidden
    >
      <g
        stroke="currentColor"
        strokeWidth="1.4"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M24 4.5 C20.8 10 20.8 14 24 18.5 C27.2 14 27.2 10 24 4.5 Z" />
        <path d="M22.5 17.5 C17 12.5 12.5 12 9.5 13 C11.5 17.5 16.5 19.8 23 20" />
        <path d="M25.5 17.5 C31 12.5 35.5 12 38.5 13 C36.5 17.5 31.5 19.8 25 20" />
        <path d="M9 22.5 C18 26 30 26 39 22.5" />
      </g>
    </svg>
  );
}

/**
 * The dinkus — diamond-tipped rules around a lotus. Set between
 * sections the way a fleuron sits between cantos.
 */
export function LotusDivider({ className = "" }: { className?: string }) {
  return (
    <div
      className={`flex items-center justify-center gap-4 text-gold ${className}`}
      aria-hidden
    >
      <svg width="88" height="8" viewBox="0 0 88 8" fill="none">
        <g stroke="currentColor" strokeWidth="1.2" strokeLinecap="round">
          <path d="M0 4 H74" opacity="0.5" />
          <path d="M80 1 L84 4 L80 7 L76 4 Z" />
        </g>
      </svg>
      <Lotus size={34} className="opacity-90" />
      <svg width="88" height="8" viewBox="0 0 88 8" fill="none">
        <g stroke="currentColor" strokeWidth="1.2" strokeLinecap="round">
          <path d="M8 1 L12 4 L8 7 L4 4 Z" />
          <path d="M14 4 H88" opacity="0.5" />
        </g>
      </svg>
    </div>
  );
}

/* One repeat of the block-print border: rule · diamond · rule · bud ·
   rule · diamond — the bel that runs along the hem of a sari or a
   hand-printed textile. */
const BAND_UNIT =
  "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='48' height='14' viewBox='0 0 48 14'%3E%3Cg stroke='%23c15f3c' stroke-width='1.1' fill='none' stroke-linecap='round'%3E%3Cpath d='M0 7 H8.5'/%3E%3Cpath d='M12 3.5 L15.5 7 L12 10.5 L8.5 7 Z'/%3E%3Cpath d='M15.5 7 H21.5'/%3E%3Cpath d='M26 11 C23.6 8.4 23.6 5 26 2.6 C28.4 5 28.4 8.4 26 11 Z'/%3E%3Cpath d='M30.5 7 H36.5'/%3E%3Cpath d='M40 3.5 L43.5 7 L40 10.5 L36.5 7 Z'/%3E%3Cpath d='M43.5 7 H48'/%3E%3C/g%3E%3C/svg%3E";

/**
 * The band — a run of block-print border, used where a chapter of the
 * page opens or closes.
 */
export function Band({ className = "" }: { className?: string }) {
  return (
    <div className={`flex justify-center ${className}`} aria-hidden>
      <div
        className="h-[14px] w-64 opacity-70 md:w-80"
        style={{
          backgroundImage: `url("${BAND_UNIT}")`,
          backgroundSize: "48px 14px",
          backgroundRepeat: "repeat-x",
          backgroundPosition: "center",
        }}
      />
    </div>
  );
}

/* The rangoli chain — small diamonds and dots, used as the decorated
   segment of a section head's rule. */
const CHAIN_UNIT =
  "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='20' height='9' viewBox='0 0 20 9'%3E%3Cg stroke='%23c15f3c' stroke-width='1' fill='none'%3E%3Cpath d='M5 1.5 L8 4.5 L5 7.5 L2 4.5 Z'/%3E%3Ccircle cx='14' cy='4.5' r='1.1' fill='%23c15f3c' stroke='none'/%3E%3C/g%3E%3C/svg%3E";

export function RangoliChain({ className = "" }: { className?: string }) {
  return (
    <span
      className={`inline-block h-[9px] w-24 opacity-70 ${className}`}
      style={{
        backgroundImage: `url("${CHAIN_UNIT}")`,
        backgroundSize: "20px 9px",
        backgroundRepeat: "repeat-x",
        backgroundPosition: "left center",
      }}
      aria-hidden
    />
  );
}
